import React, { useEffect, useState } from 'react'
import { TextField, tooltipClasses } from '@mui/material'
import { CompanyGratificationParam } from '../../../../../../types/gratifications'
import { LightTooltip } from '../../../../../LighTooltip'

interface GratificationFieldProps {
        gratification: CompanyGratificationParam
        value: number | ''
        isBlocked: boolean
        onValueChange: (value: number) => void
        onFocus?: () => void
        onBlur?: () => void
}

const GratificationField: React.FC<GratificationFieldProps> = ({
        gratification,
        value,
        isBlocked,
        onValueChange,
        onFocus,
        onBlur,
}) => {
        const [inputValue, setInputValue] = useState<string>(value === '' ? '' : String(value))
        const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const rawValue = e.target.value
                if (rawValue !== '' && !/^[0-9]*[.,]?[0-9]*$/.test(rawValue)) {
                        return
                }

                const val = rawValue.replace(/,/g, '.')
                setInputValue(val)

                // Keep empty value local while typing to avoid removing the field mid-edit.
                if (val === '' || val === '.') {
                        return
                }

                // Only call onValueChange if it's a valid number
                const floatVal = parseFloat(val)
                if (!isNaN(floatVal)) {
                        onValueChange(floatVal)
                }
        }

        const isError = gratification.valor > gratification.limite

        useEffect(() => {
                // Sync inputValue when parent value changes
                setInputValue(value === '' ? '' : String(value))
        }, [value])

        return (
                <LightTooltip
                        title={gratification.descricao}
                        placement='top'
                        enterDelay={100}
                        sx={{
                                fontFamily: 'var(--font-family)',
                                color: 'var(--primary-dark)',
                                [`& .${tooltipClasses.tooltip}`]: {
                                        transform: 'translateY(-0.25rem) !important',
                                },
                        }}
                >
                        <TextField
                                data-testid='gratification-field'
                                label={gratification.descricao}
                                size='small'
                                type='text'
                                value={inputValue}
                                multiline={false}
                                error={isError}
                                helperText={
                                        isError
                                                ? `Valor máximo permitido é ${gratification.limite.toLocaleString('pt-PT', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                        })}`
                                                : ''
                                }
                                onChange={handleValueChange}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                disabled={isBlocked}
                                inputProps={{
                                        min: 0,
                                        max: !isNaN(gratification.limite) ? gratification.limite : 0,
                                        sx: {
                                                fontSize: '0.75rem',
                                                width: '100px',
                                                color: 'var(--primary-dark)',
                                                '&::placeholder': {
                                                        color: 'var(--primary-dark)',
                                                },
                                        },
                                }}
                                sx={{
                                        '& .MuiFormLabel-root': {
                                                color: '#092056 !important',
                                                fontFamily: 'Poppins, sans-serif',
                                                fontWeight: '500 !important',
                                        },
                                        '& .Mui-disabled': {
                                                backgroundColor: 'var(--secondary-light)',
                                                borderColor: 'rgba(134, 149, 175, 0.00)',
                                                '& input': {
                                                        color: '#092056 !important',
                                                        WebkitTextFillColor: '#092056 !important', // Ensures color for disabled input
                                                },
                                        },
                                        '& input[type="number" i]::-webkit-calendar-picker-indicator': {
                                                backgroundImage: 'none',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                                fontSize: '11px',
                                                transform: 'translate(14px, -6px) scale(0.75)',
                                        },
                                }}
                        />
                </LightTooltip>
        )
}

GratificationField.displayName = 'GratificationField'

export default GratificationField
